import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, AlertCircle, Loader2, X, Package } from 'lucide-react';
import axios from 'axios';
import { useAppContext } from '../../context/AppContext';
import AuthService from '../../services/AuthService';
import OfflineService from '../../services/OfflineService';

const C = {
    primary: '#0E8164',
    accent: '#F18F27',
    tomato: '#DF2636',
    white: '#ffffff',
};

// Ventana para acumular los 5 toques (ms) y cantidad requerida
const TAP_WINDOW = 2500;
const TAPS_REQUIRED = 5;
// Intervalo de polling mientras el catálogo se prepara en segundo plano
const POLL_INTERVAL = 3000;

/**
 * Recarga de catálogo sin reiniciar el totem.
 *
 *  - 5 toques en la esquina superior derecha -> popup preguntando si sincronizar.
 *  - Al confirmar, la API intermedia prepara el catálogo en segundo plano
 *    (el totem sigue funcionando con normalidad).
 *  - Cuando está listo aparece un ícono apretable en la misma esquina.
 *  - Al apretarlo se aplica el catálogo nuevo (rápido, ya está en memoria de la API).
 */
const CatalogReloadManager = () => {
    const { sessionData, updateSessionData, settings } = useAppContext();

    const [state, setState] = useState('idle'); // idle | preparing | ready
    const [showPopup, setShowPopup] = useState(false);
    const [applying, setApplying] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const tapCountRef = useRef(0);
    const tapTimerRef = useRef(null);
    const pollRef = useRef(null);

    const apiUrl = settings?.apiUrl || 'http://localhost:9000';

    // ── Gesto de 5 toques ───────────────────────────────────────
    const handleCornerTap = useCallback(() => {
        // Si ya está listo, el toque lo maneja el ícono (no cuenta como gesto)
        if (state === 'ready') return;

        tapCountRef.current += 1;
        if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
        tapTimerRef.current = setTimeout(() => { tapCountRef.current = 0; }, TAP_WINDOW);

        if (tapCountRef.current >= TAPS_REQUIRED) {
            tapCountRef.current = 0;
            if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
            setErrorMsg('');
            setShowPopup(true);
        }
    }, [state]);

    // ── Polling de estado mientras prepara ──────────────────────
    const stopPolling = useCallback(() => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }, []);

    const startPolling = useCallback(() => {
        stopPolling();
        pollRef.current = setInterval(async () => {
            try {
                const res = await axios.get(`${apiUrl}/api/pos/catalog/status`, { timeout: 8000 });
                const st = res.data?.state;
                if (st === 'ready') {
                    stopPolling();
                    setState('ready');
                } else if (st === 'error') {
                    stopPolling();
                    setState('idle');
                    setErrorMsg(res.data?.error || 'Error preparando el catálogo');
                }
            } catch (e) {
                // Silencioso: seguimos intentando en el próximo tick
                console.warn('[CATALOG] Error consultando estado:', e.message);
            }
        }, POLL_INTERVAL);
    }, [apiUrl, stopPolling]);

    useEffect(() => () => { stopPolling(); if (tapTimerRef.current) clearTimeout(tapTimerRef.current); }, [stopPolling]);

    // ── Iniciar preparación en segundo plano ────────────────────
    const startPrepare = useCallback(async () => {
        const pin = sessionData?.pin;
        const user = AuthService.getUser();
        const userId = user ? user.user_id : null;

        if (!pin || !userId) {
            setErrorMsg('No se pudo iniciar: falta PIN o usuario');
            return;
        }

        setShowPopup(false);
        setErrorMsg('');
        setState('preparing');

        try {
            await axios.post(
                `${apiUrl}/api/pos/catalog/prepare`,
                { pin, user_id: userId },
                { timeout: 15000, headers: { 'Content-Type': 'application/json' } }
            );
            startPolling();
        } catch (e) {
            setState('idle');
            const msg = e.response?.status === 401
                ? 'PIN incorrecto'
                : (e.response?.data?.error || 'No se pudo iniciar la preparación');
            setErrorMsg(msg);
        }
    }, [apiUrl, sessionData, startPolling]);

    // ── Aplicar catálogo ya preparado ───────────────────────────
    const applyCatalog = useCallback(async () => {
        setApplying(true);
        try {
            const res = await axios.get(`${apiUrl}/api/pos/catalog/take`, { timeout: 30000 });
            const data = res.data;

            if (!data || data.ready === false || data.authenticated === false) {
                // Ya no estaba disponible: volvemos a idle para permitir reintentar
                setState('idle');
                setErrorMsg('El catálogo ya no estaba disponible, reintenta');
                return;
            }

            const pin = sessionData?.pin;
            const merged = { ...data, pin };
            updateSessionData(merged);
            OfflineService.saveSessionInfo(merged, data.config_id);

            setState('idle');
        } catch (e) {
            setErrorMsg('Error aplicando el catálogo');
        } finally {
            setApplying(false);
        }
    }, [apiUrl, sessionData, updateSessionData]);

    return (
        <>
            {/* Zona invisible de toques en la esquina superior derecha */}
            <div
                onClick={handleCornerTap}
                style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    width: '90px',
                    height: '90px',
                    zIndex: 9990,
                    background: 'transparent',
                }}
            />

            {/* Ícono de estado en la misma esquina (preparando / listo) */}
            <AnimatePresence>
                {state === 'preparing' && (
                    <motion.div
                        key="preparing"
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.6 }}
                        style={{
                            position: 'fixed', top: '14px', right: '14px', zIndex: 9995,
                            width: '52px', height: '52px', borderRadius: '50%',
                            background: 'rgba(255,255,255,0.95)',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                            style={{ display: 'flex' }}
                        >
                            <Loader2 size={26} color={C.primary} />
                        </motion.div>
                    </motion.div>
                )}

                {state === 'ready' && (
                    <motion.button
                        key="ready"
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.6 }}
                        onClick={applyCatalog}
                        disabled={applying}
                        style={{
                            position: 'fixed', top: '14px', right: '14px', zIndex: 9995,
                            width: '52px', height: '52px', borderRadius: '50%',
                            background: C.accent, border: 'none', cursor: applying ? 'wait' : 'pointer',
                            boxShadow: '0 8px 24px rgba(241,143,39,0.45)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        {applying ? (
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                style={{ display: 'flex' }}
                            >
                                <Loader2 size={24} color={C.white} />
                            </motion.div>
                        ) : (
                            <>
                                <RefreshCw size={22} color={C.white} />
                                <motion.span
                                    animate={{ scale: [1, 1.25, 1] }}
                                    transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
                                    style={{
                                        position: 'absolute', top: '-3px', right: '-3px',
                                        width: '18px', height: '18px', borderRadius: '50%',
                                        background: C.tomato, border: `2px solid ${C.white}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}
                                >
                                    <AlertCircle size={12} color={C.white} />
                                </motion.span>
                            </>
                        )}
                    </motion.button>
                )}
            </AnimatePresence>

            {/* Popup de confirmación */}
            <AnimatePresence>
                {showPopup && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={(e) => { if (e.target === e.currentTarget) setShowPopup(false); }}
                        style={{
                            position: 'fixed', inset: 0, zIndex: 9998,
                            background: 'rgba(14,129,100,0.5)', backdropFilter: 'blur(8px)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        <motion.div
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            style={{
                                background: '#fff', borderRadius: '28px', padding: '36px',
                                width: '90%', maxWidth: '420px', margin: '20px',
                                boxShadow: '0 30px 60px rgba(0,0,0,0.25)', textAlign: 'center',
                                fontFamily: 'Quicksand, system-ui, sans-serif', position: 'relative',
                            }}
                        >
                            <button
                                onClick={() => setShowPopup(false)}
                                style={{
                                    position: 'absolute', top: '18px', right: '18px',
                                    background: '#f1f5f9', border: 'none', borderRadius: '50%',
                                    width: '36px', height: '36px', cursor: 'pointer', color: '#64748b',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                            >
                                <X size={18} />
                            </button>

                            <div style={{
                                width: '68px', height: '68px', borderRadius: '50%',
                                background: 'rgba(14,129,100,0.1)', margin: '0 auto 18px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <Package size={32} color={C.primary} />
                            </div>

                            <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#1e293b', margin: '0 0 10px' }}>
                                Sincronizar catálogo
                            </h2>
                            <p style={{ fontSize: '0.95rem', color: '#64748b', fontWeight: 600, margin: '0 0 26px', lineHeight: 1.5 }}>
                                Se preparará el catálogo nuevo en segundo plano sin reiniciar el totem.
                                Cuando esté listo aparecerá un ícono para aplicarlo.
                            </p>

                            <div style={{ display: 'flex', gap: '12px' }}>
                                <button
                                    onClick={() => setShowPopup(false)}
                                    style={{
                                        flex: 1, padding: '14px', borderRadius: '14px', border: 'none',
                                        background: '#e2e8f0', color: '#64748b', fontWeight: 700,
                                        fontSize: '0.95rem', cursor: 'pointer',
                                        fontFamily: 'Quicksand, system-ui, sans-serif',
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={startPrepare}
                                    style={{
                                        flex: 1, padding: '14px', borderRadius: '14px', border: 'none',
                                        background: C.primary, color: C.white, fontWeight: 700,
                                        fontSize: '0.95rem', cursor: 'pointer',
                                        fontFamily: 'Quicksand, system-ui, sans-serif',
                                    }}
                                >
                                    Sincronizar
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Aviso de error (breve) */}
            <AnimatePresence>
                {errorMsg && (
                    <motion.div
                        initial={{ opacity: 0, y: -12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        onClick={() => setErrorMsg('')}
                        style={{
                            position: 'fixed', top: '78px', right: '14px', zIndex: 9996,
                            background: C.tomato, color: C.white, padding: '10px 16px',
                            borderRadius: '12px', fontWeight: 700, fontSize: '0.85rem',
                            maxWidth: '260px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                            fontFamily: 'Quicksand, system-ui, sans-serif', cursor: 'pointer',
                        }}
                    >
                        {errorMsg}
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default CatalogReloadManager;
